import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiSearch, FiX, FiXCircle } from "react-icons/fi";
import AppDataTable from "components/common/AppDataTable";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import StatusBadge from "components/common/StatusBadge";
import { ApiError, api, isSuccessResponse } from "lib/api";
import { confirmAction } from "lib/alerts/appAlerts";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { buildQueryString, formatTs } from "utils/helper";
import { formatPortalDateTime, noWrap } from "utils/formatters";
import RejectCancellationRequestModal from "pages/transaction/RejectCancellationRequestModal";

const DEFAULT_LIMIT = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL_VALUES = { page: 1, limit: DEFAULT_LIMIT, search: "", status: "" };

const CANCELLATION_STATUS_ACTIONS = {
  approve: {
    resolvePath: API_ENDPOINTS.cancellationRequestApprove,
    confirmTitle: "Approve cancellation?",
    confirmText: "Approve",
    errorTitle: "Approve failed",
    successTitle: "Cancellation approved",
    defaultSuccessMessage: "The cancellation request was approved.",
    defaultErrorMessage: "Could not approve the request.",
  },
  reject: {
    resolvePath: API_ENDPOINTS.cancellationRequestReject,
    confirmTitle: "Reject cancellation?",
    confirmText: "Reject",
    errorTitle: "Reject failed",
    successTitle: "Cancellation rejected",
    defaultSuccessMessage: "The cancellation request was rejected.",
    defaultErrorMessage: "Could not reject the request.",
  },
};

function formatUserName(user) {
  const first =user?.corporateProfile?.firstName ?? user?.individualProfile?.firstName ?? user?.firstName ?? "";
  const last = user?.corporateProfile?.lastName ?? user?.individualProfile?.lastName ?? user?.lastName ?? "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || "—";
}

function formatInvestmentAmount(investment) {
  const amount = investment?.amount;
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const currency = investment?.currency || "USD";
  return `${currency} ${Number(amount).toFixed(2)}`;
}

function mapCancellationRequestRow(item) {
  const investment = item.investment ?? item.transaction ?? {};
  const user = item.user ?? investment.user ?? {};

  return {
    id: item.id ?? item._id,
    user_email_id: user.email ?? "—",
    user_name: formatUserName(user),
    project_name: investment.project?.title ?? item.project?.title ?? "—",
    investment_amount: formatInvestmentAmount(investment),
    transaction_status: investment.status ?? "—",
    request_status: item.status ?? "—",
    requested_at: formatPortalDateTime(item.requestedAt ?? item.createdAt),
    reason: item?.notes ?? item.reason ?? item.cancellationReason ?? item.cancelReason ?? "",
  };
}

function RequestStatusCell(row) {
  return <StatusBadge status={row.request_status} as="span" />;
}

function TransactionStatusCell(row) {
  return <StatusBadge status={row.transaction_status} as="span" />;
}

const rowPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  request_status: PropTypes.string,
});

function isRequestStatusRequested(status) {
  return String(status ?? "").trim().toLowerCase() === "requested";
}

function CancellationRequestActions({ row, processingId, onApprove, onReject }) {
  if (!isRequestStatusRequested(row.request_status)) {
    return <span className="text-text-500">—</span>;
  }

  const busy = processingId === row.id;

  return (
    <div className="flex flex-wrap gap-1">
      <button
        type="button"
        title="Approve"
        disabled={busy}
        onClick={() => onApprove(row)}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-[#216E4E] transition hover:bg-[#E7FBF8] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiCheck size={14} />
        Approve
      </button>
      <button
        type="button"
        title="Reject"
        disabled={busy}
        onClick={() => onReject(row)}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-danger transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiX size={14} />
        Reject
      </button>
    </div>
  );
}

CancellationRequestActions.propTypes = {
  row: rowPropType.isRequired,
  processingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default function CancelRequestedTransactionsTab() {
  const [data, setData] = useState({});
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [rejectModalRow, setRejectModalRow] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [rejectionNotesError, setRejectionNotesError] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    setListError("");
  }, [filters]);

  const rows = useMemo(() => data?.data?.map(mapCancellationRequestRow) ?? [], [data]);
  const totalItems = useMemo(() => data?.total ?? 0, [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (data?.limit || filters.limit))),
    [totalItems, filters.limit, data],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQueryString(filters);
      const path = qs
        ? `${API_ENDPOINTS.cancellationRequests}?${qs}`
        : API_ENDPOINTS.cancellationRequests;
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load cancellation requests.";
        setListError(msg);
        swtAlert.error("Cancellation requests", msg);
        return;
      }
      setData(resp);
      setListError("");
    } catch (error) {
      const msg = error instanceof ApiError ? error.message : error?.message || "Could not load cancellation requests.";
      setListError(msg);
      swtAlert.error("Cancellation requests", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = useCallback(
    async (row) => {
      const action = CANCELLATION_STATUS_ACTIONS.approve;

      const ok = await confirmAction({
        title: action.confirmTitle,
        text: `${action.confirmText} the cancellation request for ${row.user_email_id}?`,
        confirmText: action.confirmText,
        cancelText: "Cancel",
      });
      if (!ok) return;

      setProcessingId(row.id);
      try {
        const resp = await api.patch(action.resolvePath(row.id));
        if (!isSuccessResponse(resp)) {
          const msg = resp?.message || action.defaultErrorMessage;
          swtAlert.error(action.errorTitle, msg);
          return;
        }
        swtAlert.success(action.successTitle, resp?.message || action.defaultSuccessMessage);
        await fetchData();
      } catch (error) {
        const msg =
          error instanceof ApiError ? error.message : error?.message || action.defaultErrorMessage;
        swtAlert.error(action.errorTitle, msg);
      } finally {
        setProcessingId(null);
      }
    },
    [fetchData],
  );

  const closeRejectModal = useCallback(() => {
    if (rejectSubmitting) return;
    setRejectModalRow(null);
    setRejectionNotes("");
    setRejectionNotesError("");
  }, [rejectSubmitting]);

  const handleReject = useCallback(async (row) => {
    const action = CANCELLATION_STATUS_ACTIONS.reject;

    const ok = await confirmAction({
      title: action.confirmTitle,
      text: `Reject the cancellation request for ${row.user_email_id}?`,
      confirmText: action.confirmText,
      cancelText: "Cancel",
    });
    if (!ok) return;

    setRejectionNotes("");
    setRejectionNotesError("");
    setRejectModalRow(row);
  }, []);

  const submitReject = useCallback(async () => {
    const notes = rejectionNotes.trim();
    if (!notes) {
      setRejectionNotesError("Rejection reason is required.");
      return;
    }
    if (!rejectModalRow) return;

    const action = CANCELLATION_STATUS_ACTIONS.reject;
    setRejectSubmitting(true);
    setProcessingId(rejectModalRow.id);
    try {
      const resp = await api.patch(action.resolvePath(rejectModalRow.id), {
        rejectionNotes: notes,
      });
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || action.defaultErrorMessage;
        swtAlert.error(action.errorTitle, msg);
        return;
      }
      swtAlert.success(action.successTitle, resp?.message || action.defaultSuccessMessage);
      closeRejectModal();
      await fetchData();
    } catch (error) {
      const msg =
        error instanceof ApiError ? error.message : error?.message || action.defaultErrorMessage;
      swtAlert.error(action.errorTitle, msg);
    } finally {
      setRejectSubmitting(false);
      setProcessingId(null);
    }
  }, [rejectionNotes, rejectModalRow, closeRejectModal, fetchData]);

  const columns = useMemo(
    () => [
      { id: "sr_no", header: noWrap("#"), cell: (_row, index) => index + 1, width: "56px" },
      { id: "user_email_id", header: noWrap("User Email"), accessor: "user_email_id", sortable: true },
      { id: "user_name", header: noWrap("User Name"), accessor: "user_name", sortable: true },
      { id: "project_name", header: noWrap("Project"), accessor: "project_name", sortable: true },
      { id: "investment_amount", header: noWrap("Amount"), accessor: "investment_amount", sortable: true },
      { id: "transaction_status", header: noWrap("Transaction Status"), cell: TransactionStatusCell, sortable: true },
      { id: "request_status", header: noWrap("Status"), cell: RequestStatusCell, sortable: true },
      { id: "requested_at", header: noWrap("Requested At"), accessor: "requested_at", sortable: true },
      { id: "reason", header: noWrap("Reason"), accessor: "reason", sortable: true },
      {
        id: "actions",
        header: noWrap("Actions"),
        cell: (row) => (
          <CancellationRequestActions
            row={row}
            processingId={processingId}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ),
        width: "200px",
      },
    ],
    [handleApprove, handleReject, processingId],
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-text-500">Total pending requests: {totalItems}</p>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  flushSearch();
                  setFilters((prev) => ({
                    ...prev,
                    search: searchDraft.trim(),
                    page: 1,
                  }));
                }
              }}
              placeholder="Search by Email, User Name..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
            />
          </div>
          {(searchDraft || filters.search) && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setSearchDraft("");
                setFilters((prev) => ({ ...prev, search: "", page: 1 }));
              }}
              className="rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiXCircle />
            </button>
          )}
        </div>
      </div>

      {listError ? (
        <ListFetchErrorBanner message={listError} onRetry={fetchData} loading={loading} />
      ) : null}

      <AppDataTable columns={columns} data={rows} rowKey="id" minWidth={1400} loading={loading} />
      <AppPagination
        currentPage={filters.page}
        totalPages={totalPages}
        onPageChange={(page) => setFilters({ ...filters, page })}
        totalItems={totalItems}
        pageSize={filters.limit}
        onPageSizeChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />

      <RejectCancellationRequestModal
        open={Boolean(rejectModalRow)}
        request={rejectModalRow}
        rejectionNotes={rejectionNotes}
        notesError={rejectionNotesError}
        submitting={rejectSubmitting}
        onNotesChange={(event) => {
          setRejectionNotes(event.target.value);
          if (rejectionNotesError) setRejectionNotesError("");
        }}
        onClose={closeRejectModal}
        onSubmit={submitReject}
      />
    </div>
  );
}
